// ─────────────────────────────────────────────────────────────────────────────
// USER TRADING RULES — CATALOG + EVALUATOR
//
// Defines the supported rule types and the logic for checking whether a given
// rule is satisfied in a given context.
//
// CATALOG (RULE_TYPES):
//   Each entry describes one rule type — its label, parameters, default values,
//   and check function. To add a new rule type, add an entry here. Existing
//   user data persists; only the catalog grows.
//
// CONTEXT (the input to check functions):
//   {
//     trade:                     // the trade being checked (optional, for retro/realtime trade-specific checks)
//     today_trades:              // array of trades already completed today
//     today_pnl:                 // running net P&L today
//     today_loss_count:          // number of losing trades today
//     now:                       // current time as "HH:MM:SS" string
//     today_date:                // "YYYY-MM-DD" (active CME session day)
//     news_events:               // upcoming news events array [{title, time, impact}]
//   }
//
// CHECK RETURN:
//   { violated: boolean, reason: string, detail?: object }
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function tradeNet(t) {
  return (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
}

function timeToSeconds(s) {
  if (!s) return 0;
  const m = String(s).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return 0;
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] || 0);
}

function isTimeInWindow(time, startTime, endTime) {
  const t = timeToSeconds(time);
  const s = timeToSeconds(startTime);
  const e = timeToSeconds(endTime);
  if (s <= e) return t >= s && t <= e;
  // Window crosses midnight
  return t >= s || t <= e;
}

function formatTime(time) {
  return String(time || "").slice(0, 5);
}

// ─────────────────────────────────────────────────────────────────────────────
// THE CATALOG
// ─────────────────────────────────────────────────────────────────────────────

export const RULE_TYPES = {

  // ── HARD CAPS ─────────────────────────────────────────────────────────

  max_losses_per_day: {
    id: "max_losses_per_day",
    label: "Stop after N losing trades",
    short_label: "Max losses",
    description: "Stop trading after a set number of losses in one session.",
    category: "limits",
    icon: "🛑",
    multiple: false,                       // user can only have ONE of these
    params: [
      { name: "max_losses", type: "int", label: "Maximum losses", default: 3, min: 1, max: 20 },
    ],
    default_severity: "warn",
    
    check(rule, ctx) {
      const max = Number(rule.parameters?.max_losses) || 3;
      const lossesNow = ctx.today_loss_count || 0;
      // Pre-trade: about to take the (max+1)th loss? Warn now (you'll exceed).
      // Retroactive: trade already happened; check if this trade would push over.
      const violated = lossesNow >= max;
      return {
        violated,
        reason: violated
          ? `You've already taken ${lossesNow} losses today (your limit: ${max}).`
          : `${lossesNow} of ${max} losses used.`,
        detail: { current: lossesNow, limit: max },
      };
    },
    
    headline(rule, ctx) {
      const max = Number(rule.parameters?.max_losses) || 3;
      const cur = ctx.today_loss_count || 0;
      return { label: "Losses", current: cur, limit: max };
    },
  },

  max_trades_per_day: {
    id: "max_trades_per_day",
    label: "Stop after N total trades",
    short_label: "Max trades",
    description: "Hard cap on total trades per session.",
    category: "limits",
    icon: "🎯",
    multiple: false,
    params: [
      { name: "max_trades", type: "int", label: "Maximum trades", default: 5, min: 1, max: 100 },
    ],
    default_severity: "warn",
    
    check(rule, ctx) {
      const max = Number(rule.parameters?.max_trades) || 5;
      const count = ctx.today_trades?.length || 0;
      const violated = count >= max;
      return {
        violated,
        reason: violated
          ? `You've already taken ${count} trades today (your limit: ${max}).`
          : `${count} of ${max} trades used.`,
        detail: { current: count, limit: max },
      };
    },
    
    headline(rule, ctx) {
      const max = Number(rule.parameters?.max_trades) || 5;
      const cur = ctx.today_trades?.length || 0;
      return { label: "Trades", current: cur, limit: max };
    },
  },

  max_daily_loss_dollars: {
    id: "max_daily_loss_dollars",
    label: "Stop after $X loss",
    short_label: "Daily loss limit",
    description: "Hard daily loss limit, in dollars.",
    category: "limits",
    icon: "💸",
    multiple: false,
    params: [
      { name: "max_loss", type: "currency", label: "Max daily loss ($)", default: 500, min: 50, max: 10000 },
    ],
    default_severity: "warn",
    
    check(rule, ctx) {
      const max = Number(rule.parameters?.max_loss) || 500;
      const pnl = ctx.today_pnl || 0;
      const violated = pnl <= -max;
      return {
        violated,
        reason: violated
          ? `You're down $${Math.abs(pnl).toFixed(0)} today (your limit: $${max}).`
          : pnl < 0
            ? `Down $${Math.abs(pnl).toFixed(0)} of $${max} allowed.`
            : `Up $${pnl.toFixed(0)} today.`,
        detail: { current: pnl, limit: max },
      };
    },
    
    headline(rule, ctx) {
      const max = Number(rule.parameters?.max_loss) || 500;
      return { label: "Daily P&L", current: ctx.today_pnl || 0, limit: -max, format: "currency" };
    },
  },

  max_daily_drawdown: {
    id: "max_daily_drawdown",
    label: "Stop after $X drawdown from peak",
    short_label: "Daily drawdown",
    description: "Stop trading if your daily P&L falls $X from its high point. Protects your gains.",
    category: "limits",
    icon: "📉",
    multiple: false,
    params: [
      { name: "max_drawdown", type: "currency", label: "Max drawdown from peak ($)", default: 250, min: 50, max: 5000 },
    ],
    default_severity: "warn",

    check(rule, ctx) {
      const max = Number(rule.parameters?.max_drawdown) || 250;
      // Reconstruct running P&L through today's trades to find the peak.
      // ctx.today_trades is the day's trades chronologically; today_pnl is the cumulative.
      const trades = ctx.today_trades || [];
      let running = 0;
      let peak = 0;
      for (const t of trades) {
        const net = (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
        running += net;
        if (running > peak) peak = running;
      }
      const drawdown = peak - running;  // positive = how far below peak we are
      const violated = drawdown >= max;
      return {
        violated,
        reason: violated
          ? `Down $${drawdown.toFixed(0)} from today's peak (your limit: $${max}).`
          : trades.length === 0
            ? `No trades yet today.`
            : drawdown > 0
              ? `Down $${drawdown.toFixed(0)} from peak of $${peak.toFixed(0)}. Limit: $${max}.`
              : `At today's peak of $${peak.toFixed(0)}.`,
        detail: { peak, current: running, drawdown, limit: max },
      };
    },

    headline(rule, ctx) {
      const max = Number(rule.parameters?.max_drawdown) || 250;
      const trades = ctx.today_trades || [];
      let running = 0;
      let peak = 0;
      for (const t of trades) {
        const net = (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
        running += net;
        if (running > peak) peak = running;
      }
      return { label: "Drawdown", current: peak - running, limit: max, format: "currency" };
    },
  },

  // ── TIME WINDOWS ──────────────────────────────────────────────────────

  blackout_time: {
    id: "blackout_time",
    label: "No trading during specific times",
    short_label: "Blackout window",
    description: "Block trading during volatile or low-edge time windows (e.g. the open).",
    category: "timing",
    icon: "🚫",
    multiple: true,                        // user can have multiple blackouts
    params: [
      { name: "start_time", type: "time", label: "Start time (ET)", default: "09:30" },
      { name: "end_time",   type: "time", label: "End time (ET)",   default: "09:45" },
      { name: "label",      type: "string", label: "Label",          default: "Open volatility" },
    ],
    default_severity: "warn",
    
    check(rule, ctx) {
      const start = rule.parameters?.start_time || "09:30";
      const end   = rule.parameters?.end_time   || "09:45";
      const label = rule.parameters?.label || "blackout window";
      const checkTime = ctx.trade?.entry_time || ctx.now;
      if (!checkTime) return { violated: false, reason: "No time available to check." };
      const inWindow = isTimeInWindow(checkTime, start, end);

      // If we have a trade context, "violated" means an actual logged trade
      // happened in the blackout. If we're just checking live time (no trade),
      // this is informational status only — not a true violation.
      const isLiveStatusCheck = !ctx.trade;

      return {
        violated: inWindow,
        status_only: isLiveStatusCheck,
        reason: inWindow
          ? (isLiveStatusCheck
              ? `Currently in "${label}" blackout. Clears at ${formatTime(end)}.`
              : `${formatTime(checkTime)} was during your "${label}" blackout (${formatTime(start)}–${formatTime(end)}).`)
          : `Not in "${label}" blackout window.`,
        detail: { window_start: start, window_end: end, label },
      };
    },
    
    headline(rule, ctx) {
      const start = rule.parameters?.start_time || "09:30";
      const end   = rule.parameters?.end_time   || "09:45";
      const label = rule.parameters?.label || "Blackout";
      const inWindow = ctx.now ? isTimeInWindow(ctx.now, start, end) : false;
      return {
        label,
        text: inWindow ? `IN blackout` : `Clear (${formatTime(start)}–${formatTime(end)})`,
        ok: !inWindow,
      };
    },
  },

  allowed_hours: {
    id: "allowed_hours",
    label: "Only trade during specific hours",
    short_label: "Trading window",
    description: "Block trading outside your A+ window. Useful while building consistency.",
    category: "timing",
    icon: "⏰",
    multiple: false,                       // single allowed window per user
    params: [
      { name: "start_time", type: "time", label: "Trade only after (ET)",  default: "09:45" },
      { name: "end_time",   type: "time", label: "Stop trading by (ET)",   default: "11:00" },
    ],
    default_severity: "warn",
    
    check(rule, ctx) {
      const start = rule.parameters?.start_time || "09:45";
      const end   = rule.parameters?.end_time   || "11:00";
      const checkTime = ctx.trade?.entry_time || ctx.now;
      if (!checkTime) return { violated: false, reason: "No time available to check." };
      const inWindow = isTimeInWindow(checkTime, start, end);
      const isLiveStatusCheck = !ctx.trade;

      // Compute time-to-open or time-to-close for friendlier messages
      let nextChange = "";
      if (isLiveStatusCheck) {
        const checkSec = timeToSeconds(checkTime);
        const startSec = timeToSeconds(start);
        const endSec   = timeToSeconds(end);
        if (inWindow) {
          // We're inside; closes at end
          const minsLeft = Math.floor((endSec - checkSec) / 60);
          nextChange = minsLeft > 0
            ? ` Closes in ${minsLeft}m.`
            : "";
        } else {
          // Outside; opens at start (handle midnight crossing)
          const minsUntilOpen = startSec >= checkSec
            ? Math.floor((startSec - checkSec) / 60)
            : Math.floor((86400 - checkSec + startSec) / 60);
          if (minsUntilOpen < 60) {
            nextChange = ` Opens in ${minsUntilOpen}m.`;
          } else {
            const hrs = Math.floor(minsUntilOpen / 60);
            const mins = minsUntilOpen % 60;
            nextChange = ` Opens at ${formatTime(start)} (in ${hrs}h ${mins}m).`;
          }
        }
      }

      return {
        violated: !inWindow,
        status_only: isLiveStatusCheck,
        reason: inWindow
          ? (isLiveStatusCheck
              ? `Currently inside your trade window.${nextChange}`
              : `${formatTime(checkTime)} was inside your allowed window.`)
          : (isLiveStatusCheck
              ? `Currently outside your trade window (${formatTime(start)}–${formatTime(end)}).${nextChange}`
              : `${formatTime(checkTime)} was outside your trade window (${formatTime(start)}–${formatTime(end)}).`),
        detail: { window_start: start, window_end: end },
      };
    },
    
    headline(rule, ctx) {
      const start = rule.parameters?.start_time || "09:45";
      const end   = rule.parameters?.end_time   || "11:00";
      const inWindow = ctx.now ? isTimeInWindow(ctx.now, start, end) : false;
      return {
        label: "Trade window",
        text: inWindow ? `Open (closes ${formatTime(end)})` : `Closed (opens ${formatTime(start)})`,
        ok: inWindow,
      };
    },
  },

  // ── NEWS EVENTS ───────────────────────────────────────────────────────

  news_blackout: {
    id: "news_blackout",
    label: "No trading around scheduled news",
    short_label: "News blackout",
    description: "Block trading X minutes before and after major scheduled events (CPI, FOMC, NFP).",
    category: "events",
    icon: "📰",
    multiple: false,
    params: [
      { name: "minutes_before", type: "int", label: "Minutes before event", default: 15, min: 0, max: 120 },
      { name: "minutes_after",  type: "int", label: "Minutes after event",  default: 15, min: 0, max: 120 },
      { name: "events",         type: "multiselect", label: "Events to avoid",
        options: ["CPI", "FOMC", "NFP", "PCE", "GDP", "Retail Sales", "Unemployment Rate"],
        default: ["CPI", "FOMC", "NFP"] },
    ],
    default_severity: "warn",
    
    check(rule, ctx) {
      const before = Number(rule.parameters?.minutes_before) || 15;
      const after  = Number(rule.parameters?.minutes_after) || 15;
      const events = rule.parameters?.events || ["CPI", "FOMC", "NFP"];
      const checkTime = ctx.trade?.entry_time || ctx.now;
      const today = ctx.today_date;
      const newsEvents = ctx.news_events || [];
      const isLiveStatusCheck = !ctx.trade;
      
      if (!checkTime || !today) {
        return { violated: false, status_only: isLiveStatusCheck, reason: "No time available to check." };
      }
      
      // Find any matching event on today's date
      for (const ev of newsEvents) {
        if (!events.some(e => (ev.title || "").toLowerCase().includes(e.toLowerCase()))) continue;
        const evDate = (ev.date || ev.published_at || "").slice(0, 10);
        if (evDate !== today) continue;
        const evTime = ev.time || (ev.published_at || "").slice(11, 19);
        if (!evTime) continue;
        
        const evSec    = timeToSeconds(evTime);
        const checkSec = timeToSeconds(checkTime);
        if (checkSec >= evSec - before * 60 && checkSec <= evSec + after * 60) {
          return {
            violated: true,
            status_only: isLiveStatusCheck,
            reason: isLiveStatusCheck
              ? `Currently in blackout for ${ev.title} (at ${formatTime(evTime)}).`
              : `${ev.title} at ${formatTime(evTime)} — blackout window is ${before}m before / ${after}m after.`,
            detail: { event: ev, window_before: before, window_after: after },
          };
        }
      }
      
      return {
        violated: false,
        status_only: isLiveStatusCheck,
        reason: "No news blackout currently active.",
      };
    },
    
    headline(rule, ctx) {
      const events = rule.parameters?.events || [];
      const before = Number(rule.parameters?.minutes_before) || 15;
      const newsEvents = ctx.news_events || [];
      const today = ctx.today_date;
      const now = ctx.now;
      
      // Find the next matching event today
      let nextEv = null;
      let nextEvTime = null;
      for (const ev of newsEvents) {
        if (!events.some(e => (ev.title || "").toLowerCase().includes(e.toLowerCase()))) continue;
        const evDate = (ev.date || ev.published_at || "").slice(0, 10);
        if (evDate !== today) continue;
        const evTime = ev.time || (ev.published_at || "").slice(11, 19);
        if (!evTime) continue;
        if (timeToSeconds(evTime) < timeToSeconds(now || "00:00")) continue;
        if (!nextEv || timeToSeconds(evTime) < timeToSeconds(nextEvTime)) {
          nextEv = ev; nextEvTime = evTime;
        }
      }
      
      return {
        label: "News",
        text: nextEv ? `Next: ${nextEv.title} at ${formatTime(nextEvTime)}` : "No upcoming events today",
        ok: !nextEv || timeToSeconds(nextEvTime) > timeToSeconds(now || "00:00") + before * 60,
      };
    },
  },

};

// ─────────────────────────────────────────────────────────────────────────────
// EVALUATOR
//
// Given a user's active rules and a context, returns the list of (rule, result)
// pairs. The caller decides what to do with them.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Evaluate a single rule against a context.
 */
export function evaluateRule(rule, context) {
  if (!rule || !rule.enabled) return null;
  const def = RULE_TYPES[rule.rule_type];
  if (!def) return { rule, error: `Unknown rule type: ${rule.rule_type}` };
  try {
    const result = def.check(rule, context);
    return { rule, def, result };
  } catch (e) {
    return { rule, def, error: e.message };
  }
}

/**
 * Evaluate ALL of a user's active rules against the given context.
 * Returns an array of evaluations sorted with violated rules first.
 */
export function evaluateAllRules(userRules, context) {
  if (!Array.isArray(userRules)) return [];
  const results = userRules
    .map(r => evaluateRule(r, context))
    .filter(Boolean);
  
  // Sort: violated first, then by rule type id for stable display
  results.sort((a, b) => {
    const av = a.result?.violated ? 1 : 0;
    const bv = b.result?.violated ? 1 : 0;
    if (av !== bv) return bv - av;
    return (a.rule.rule_type || "").localeCompare(b.rule.rule_type || "");
  });
  
  return results;
}

/**
 * Build a context object for evaluating rules at the current moment.
 * Used by the Dashboard widget and Trade Entry pre-submit check.
 */
export function buildLiveContext({ trades, now, today_date, news_events }) {
  const todayTrades = (trades || []).filter(t =>
    String(t.trade_date || "").slice(0, 10) === today_date
  );
  const todayPnl = todayTrades.reduce((s, t) => s + tradeNet(t), 0);
  const todayLossCount = todayTrades.filter(t => t.result === "Loss").length;
  
  return {
    today_trades: todayTrades,
    today_pnl: todayPnl,
    today_loss_count: todayLossCount,
    now: now || new Date().toTimeString().slice(0, 8),
    today_date,
    news_events: news_events || [],
  };
}

/**
 * Build a context object for evaluating rules against a specific (historical)
 * trade — used for retroactive analytics and for trade-entry pre-submit check.
 */
export function buildTradeContext({ trade, trades, news_events }) {
  const tradeDate = String(trade.trade_date || "").slice(0, 10);
  
  // Trades on the same day, sorted chronologically, that happened BEFORE this one
  const sameDayTrades = (trades || []).filter(t =>
    String(t.trade_date || "").slice(0, 10) === tradeDate
  );
  const tradeIdx = sameDayTrades.findIndex(t => t.id === trade.id);
  const priorTrades = tradeIdx >= 0
    ? sameDayTrades.slice(0, tradeIdx)
    : sameDayTrades.filter(t =>
        timeToSeconds(t.entry_time) < timeToSeconds(trade.entry_time)
      );
  
  const priorPnl   = priorTrades.reduce((s, t) => s + tradeNet(t), 0);
  const priorLosses = priorTrades.filter(t => t.result === "Loss").length;
  
  return {
    trade,
    today_trades: priorTrades,
    today_pnl: priorPnl,
    today_loss_count: priorLosses,
    now: trade.entry_time,
    today_date: tradeDate,
    news_events: news_events || [],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// PRESET BUNDLES
//
// Quick-start rule sets. Used by the "Apply preset" button in Settings.
// Returns an array of rule objects (without IDs — caller will insert them).
// ─────────────────────────────────────────────────────────────────────────────

export const RULE_PRESETS = {
  topstep_safe: {
    id: "topstep_safe",
    name: "TopStep-Safe",
    description: "Rules calibrated for a TopStep $50K combine — daily loss buffer, modest trade cap, news blackouts.",
    rules: [
      { rule_type: "max_daily_loss_dollars", parameters: { max_loss: 700 }, severity: "warn" },
      { rule_type: "max_daily_drawdown",     parameters: { max_drawdown: 250 }, severity: "warn" },
      { rule_type: "max_trades_per_day",     parameters: { max_trades: 8 }, severity: "warn" },
      { rule_type: "max_losses_per_day",     parameters: { max_losses: 3 }, severity: "warn" },
      { rule_type: "blackout_time",          parameters: { start_time: "09:30", end_time: "09:45", label: "Open volatility" }, severity: "warn" },
      { rule_type: "news_blackout",          parameters: { minutes_before: 15, minutes_after: 15, events: ["CPI", "FOMC", "NFP"] }, severity: "warn" },
    ],
  },
  
  apex_safe: {
    id: "apex_safe",
    name: "Apex-Safe",
    description: "Rules calibrated for an Apex $50K eval — focus on consistency. Apex's trailing drawdown makes peak-protection critical.",
    rules: [
      { rule_type: "max_daily_drawdown", parameters: { max_drawdown: 400 }, severity: "warn" },
      { rule_type: "max_trades_per_day", parameters: { max_trades: 10 }, severity: "warn" },
      { rule_type: "max_losses_per_day", parameters: { max_losses: 4 }, severity: "warn" },
      { rule_type: "blackout_time",      parameters: { start_time: "09:30", end_time: "09:45", label: "Open volatility" }, severity: "warn" },
      { rule_type: "news_blackout",      parameters: { minutes_before: 15, minutes_after: 15, events: ["CPI", "FOMC", "NFP"] }, severity: "warn" },
    ],
  },
  
  conservative: {
    id: "conservative",
    name: "Conservative",
    description: "Build consistency first. Tight trade caps, hard daily loss, drawdown protection, narrow A+ window.",
    rules: [
      { rule_type: "max_daily_loss_dollars", parameters: { max_loss: 400 }, severity: "warn" },
      { rule_type: "max_daily_drawdown",     parameters: { max_drawdown: 200 }, severity: "warn" },
      { rule_type: "max_trades_per_day",     parameters: { max_trades: 5 }, severity: "warn" },
      { rule_type: "max_losses_per_day",     parameters: { max_losses: 2 }, severity: "warn" },
      { rule_type: "allowed_hours",          parameters: { start_time: "09:45", end_time: "11:00" }, severity: "warn" },
      { rule_type: "news_blackout",          parameters: { minutes_before: 20, minutes_after: 20, events: ["CPI", "FOMC", "NFP", "PCE"] }, severity: "warn" },
    ],
  },
};

/**
 * Convenience: get default parameters for a new rule of a given type.
 */
export function defaultParamsFor(ruleType) {
  const def = RULE_TYPES[ruleType];
  if (!def) return {};
  const out = {};
  for (const p of def.params) out[p.name] = p.default;
  return out;
}

/**
 * Convenience: list of rule types grouped by category, for picker UI.
 */
export function listRuleCatalog() {
  const grouped = {};
  for (const def of Object.values(RULE_TYPES)) {
    if (!grouped[def.category]) grouped[def.category] = [];
    grouped[def.category].push(def);
  }
  return grouped;
}
